import React, { useState } from 'react';

import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { capitalize, lowerCase, startCase } from 'lodash';

import { Alert, Button } from '@shared/components/bootstrap';
import { Spacer } from '@shared/components/helpers';
import { ConfirmationModal } from '@shared/components/modals/confirmation_modal';

import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { CreateTicket as CreateTechSupportTicket } from '@admin/components/tickets/tech_support/create_ticket';
import { Timestamp } from '@admin/components/timestamp';
import { logisticsEditLoadURL } from '@admin/config/routes';
import { LOGISTICS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  LoadTypeEnum,
  LogisticsLoadQuery,
  Status,
  Ticket__TicketableEnum,
  useCancelLogisticsLoadMutation,
  useReopenLogisticsLoadMutation,
  Logistics__PalletHeightPreferenceEnum,
  LogisticsLoadDocument,
  PaginatedLoadDocument,
  Logistics__SchedulingOptionEnum,
  Logistics__HandlingMethodEnum,
} from '@admin/schema';

import { LoadRequestReviewedModal } from '../load_request_reviewed_modal';
import { AddressInfo } from './address_info';

type LogisticsLoad = LogisticsLoadQuery['load'];

const BiggerText = styled.div`
  font-size: 16px;
`;

export const LogisticsLoadBasicInfoPanel: React.FC<{ load: LogisticsLoad }> = ({ load }) => {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [error, setError] = useState<string | null | undefined>(undefined);
  const [cancelModalVisible, setCancelModalVisible] = useState<boolean>(false);
  const [requestReviewedModalVisible, setRequestReviewedModalVisible] = useState<boolean>(false);

  const [executeCancel, { loading: loadingCancel }] = useCancelLogisticsLoadMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [{ query: LogisticsLoadDocument, variables: { id: load.id } }, { query: PaginatedLoadDocument }],
  });

  const [executeReopen, { loading: loadingReopen }] = useReopenLogisticsLoadMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [{ query: LogisticsLoadDocument, variables: { id: load.id } }, { query: PaginatedLoadDocument }],
  });

  const cancelLoad = async () => {
    const result = await executeCancel({
      variables: {
        input: {
          id: load.id,
        },
      },
    });

    if (
      result.data?.cancelLogisticsLoad.status === Status.Unprocessable ||
      result.data?.cancelLogisticsLoad.status === Status.Unauthorized
    ) {
      setError(result.data.cancelLogisticsLoad.error);
    } else {
      setError(undefined);
    }
  };

  const reOpenLoad = async () => {
    const result = await executeReopen({
      variables: {
        id: load.id,
      },
    });

    if (
      result.data?.reopenLogisticsLoad.status === Status.Unprocessable ||
      result.data?.reopenLogisticsLoad.status === Status.Unauthorized
    ) {
      setError(result.data.reopenLogisticsLoad.error);
    } else {
      setError(undefined);
    }
  };

  return (
    <Panel>
      {error && <Alert style="danger">{error}</Alert>}
      <Panel.Header>
        <Panel.Collapse collapsed={collapsed} onChange={setCollapsed} />
        <Panel.Title>
          Load {load.number} Details
          <Permission resource={LOGISTICS.resource} action={LOGISTICS.actions.update} denied={null}>
            <span style={{ float: 'right' }}>
              <CreateTechSupportTicket
                ticketable={{
                  id: load.id,
                  type: Ticket__TicketableEnum.LogisticsLoad,
                  displayName: 'Load #' + load.number,
                }}
              />
            </span>
          </Permission>
        </Panel.Title>
      </Panel.Header>
      <Panel.Body>
        {!collapsed && (
          <>
            <div className="row">
              <div className="col-md-6">
                <h3>Basic Info</h3>
                <ul className="list-unstyled">
                  <li>
                    <strong>Number:</strong>
                    {` ${load.number}`}
                  </li>
                  <li>
                    <strong>Account:</strong>
                    {load.account && `${load.account.customer.name} (${load.account.id})`}
                  </li>
                  <li>
                    <strong>Type:</strong>
                    {` ${load.type}`}
                  </li>
                  <li>
                    <strong>State:</strong>
                    {` ${capitalize(load.state)}`}
                  </li>
                  <li>
                    <strong>Source:</strong>
                    {` ${startCase(lowerCase(load.source))}`}
                  </li>
                  <li>
                    <strong>Warehouse:</strong>
                    {` ${load.warehouse.name}`}
                  </li>
                  <li>
                    <strong>Bill of Lading Number:</strong>
                    {` ${load.billOfLadingNumber || ''}`}
                  </li>
                  <li>
                    <strong>Handling Method:</strong>
                    {` ${load.handlingMethod.split('_').map(capitalize).join(' ')}`}
                  </li>
                  <li>
                    <strong>Expected Orders:</strong>
                    {` ${load.expectedOrders}`}
                  </li>
                  <li>
                    <strong>Expected Items:</strong>
                    {` ${load.expectedItems}`}
                  </li>
                  <li>
                    <strong>Expected Pallets:</strong>
                    {` ${load.expectedPallets}`}
                  </li>

                  <Spacer height="16px" />

                  {load.type === LoadTypeEnum.Outbound && (
                    <li>
                      <strong>{'Recipient: '}</strong>
                      {load.shipTo?.businessName && `${load.shipTo.businessName}`}
                      {load.recipientEmail && ` | ${load.recipientEmail}`}
                      {load.recipientPhone && ` | ${load.recipientPhone}`}
                    </li>
                  )}

                  {load.type === LoadTypeEnum.Outbound && (
                    <>
                      {(load.authorizedToShipShort === true || load.authorizedToShipShort === false) && (
                        <>
                          <Spacer height="16px" />
                          <li>
                            <strong>
                              If all{' '}
                              {load.handlingMethod === Logistics__HandlingMethodEnum.Palletized ? 'pallets' : 'items'}{' '}
                              do not fit on the truck, is the load authorized to ship short?{' '}
                            </strong>
                            {load.authorizedToShipShort ? 'Yes' : 'No'}
                          </li>
                        </>
                      )}

                      {(load.loadWeightRequired === true || load.loadWeightRequired === false) && (
                        <>
                          <Spacer height="16px" />
                          <li>
                            <strong>Do you require us to provide the full weight of the load? </strong>
                            {load.loadWeightRequired ? 'Yes' : 'No'}
                          </li>
                        </>
                      )}

                      {(load.loadDimensionsRequired === true || load.loadDimensionsRequired === false) && (
                        <>
                          <Spacer height="16px" />
                          <li>
                            <strong>Do you require us to provide the full dimensions of the load? </strong>
                            {load.loadDimensionsRequired ? 'Yes' : 'No'}
                          </li>
                        </>
                      )}

                      {(load.shipmentToAmazon === true || load.shipmentToAmazon === false) && (
                        <>
                          <Spacer height="16px" />
                          <li>
                            <strong>Are you shipping to Amazon? </strong>
                            {load.shipmentToAmazon
                              ? `Yes, the reference number is: ${load.amazonReferenceNumber}`
                              : 'No'}
                          </li>
                        </>
                      )}

                      {(load.reworkRequired === true || load.reworkRequired === false) && (
                        <>
                          <Spacer height="16px" />
                          <li>
                            <strong>Is any additional pallet rework required to be done before shipping? </strong>
                            {load.reworkRequired ? `Yes, ${load.reworkInstructions}` : 'No'}
                          </li>
                          {load.reworkRequired && load.targetPalletHeight && (
                            <>
                              <Spacer height="16px" />
                              <li>
                                <strong>Target Pallet Height: </strong>
                                {`${load.targetPalletHeight}″ ${
                                  load.palletHeightPreference === Logistics__PalletHeightPreferenceEnum.DoNotExceed
                                    ? '(Do not exceed)'
                                    : load.palletHeightPreference ===
                                        Logistics__PalletHeightPreferenceEnum.CloseAsPossible
                                      ? '(Match as close as possible)'
                                      : ''
                                }`}
                              </li>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {load.type === LoadTypeEnum.Inbound && (
                    <>
                      {(load.cartonsNeedToBeStoredUpright === true || load.cartonsNeedToBeStoredUpright === false) && (
                        <>
                          <Spacer height="16px" />
                          <li>
                            <strong>Do cartons need to be stored upright? </strong>
                            {load.cartonsNeedToBeStoredUpright ? 'Yes' : 'No'}
                          </li>
                        </>
                      )}

                      {(load.anyCartonExceedsFiftyPounds === true || load.anyCartonExceedsFiftyPounds === false) && (
                        <>
                          <Spacer height="16px" />
                          <li>
                            <strong>Do any cartons exceed 50 lbs? </strong>
                            {load.anyCartonExceedsFiftyPounds ? 'Yes' : 'No'}
                          </li>
                        </>
                      )}

                      {load.handlingMethod === Logistics__HandlingMethodEnum.Palletized && (
                        <>
                          <Spacer height="16px" />
                          {load.expectedPalletDimensionsOnArrival && (
                            <li>
                              <strong>Expected Pallet Dimensions on Arrival: </strong>
                              {load.expectedPalletDimensionsOnArrival}
                            </li>
                          )}
                        </>
                      )}
                      {load.handlingMethod === Logistics__HandlingMethodEnum.FloorLoaded && (
                        <>
                          {load.targetPalletHeight && (
                            <>
                              <Spacer height="16px" />
                              <li>
                                <strong>Target Pallet Height: </strong>
                                {`${load.targetPalletHeight}″ ${
                                  load.palletHeightPreference === Logistics__PalletHeightPreferenceEnum.DoNotExceed
                                    ? '(Do not exceed)'
                                    : load.palletHeightPreference ===
                                        Logistics__PalletHeightPreferenceEnum.CloseAsPossible
                                      ? '(Match as close as possible)'
                                      : ''
                                }`}
                              </li>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}

                  <Spacer height="16px" />

                  <li>
                    <strong>Special Instructions:</strong>
                    <div style={{ whiteSpace: 'pre-wrap', margin: '8px' }}>{`${
                      load.rawSpecialInstructions || ''
                    }`}</div>
                  </li>

                  <li>
                    <strong>Dock App Instructions:</strong>
                    <div style={{ whiteSpace: 'pre-wrap', margin: '8px' }}>{`${load.specialInstructions || ''}`}</div>
                  </li>
                </ul>
              </div>
              <div className="col-md-6">
                <h3>Carrier Info</h3>
                <ul className="list-unstyled">
                  <li>
                    <strong>Mode:</strong>
                    {` ${load.mode}`}
                  </li>
                  <li>
                    <strong>Trailer Number:</strong>
                    {` ${load.trailerNumber || ''}`}
                  </li>
                  <li>
                    <strong>Freight Charge Terms:</strong>
                    {` ${startCase(load.freightChargeTerms)}`}
                  </li>
                  <li>
                    <strong>Drop Trailer/Container?</strong> {load.drop ? 'Yes' : 'No'}
                  </li>
                  <li>
                    <strong>Carrier Name:</strong>
                    {` ${load.carrierDetails?.businessName || ''}`}
                  </li>
                  {load.carrierDetails && (
                    <>
                      {load.carrierDetails.transportationArrangedBy && (
                        <li>
                          <strong>Transportation Arranged By:</strong>
                          {` ${startCase(lowerCase(load.carrierDetails.transportationArrangedBy))}`}
                        </li>
                      )}
                      {load.carrierDetails.contactName && (
                        <li>
                          <strong>Contact Name:</strong>
                          {` ${load.carrierDetails.contactName}`}
                        </li>
                      )}
                      {load.carrierDetails.contactEmail && (
                        <li>
                          <strong>Contact Email:</strong>
                          {` ${load.carrierDetails.contactEmail}`}
                        </li>
                      )}
                      {load.carrierDetails.contactPhone && (
                        <li>
                          <strong>Contact Phone:</strong>
                          {` ${load.carrierDetails.contactPhone}`}
                        </li>
                      )}
                      {load.carrierDetails.scac && (
                        <li>
                          <strong>SCAC:</strong>
                          {` ${load.carrierDetails.scac}`}
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>
            <div className="row">
              {load.appointmentDate ? (
                <div className="col-md-6">
                  <h3>Appointment Date</h3>
                  <BiggerText>
                    <Timestamp value={load.appointmentDate} timezone={load.warehouse.tz} />
                  </BiggerText>
                </div>
              ) : (
                <div className="col-md-6">
                  <h3>Requested Appointment Date</h3>
                  {(load.schedulingOption === Logistics__SchedulingOptionEnum.SpecificDate ||
                    !load.schedulingOption) && (
                    <BiggerText>
                      {DateTime.fromISO(load.requestedAppointmentDate).toLocaleString(DateTime.DATE_MED)} -{' '}
                      {startCase(lowerCase(load.requestedAppointmentWindow))}
                    </BiggerText>
                  )}
                  {load.schedulingOption === Logistics__SchedulingOptionEnum.CarrierWillContactClutter &&
                    'Carrier will reach out to Clutter to arrange a date'}
                  {load.schedulingOption === Logistics__SchedulingOptionEnum.ClutterWillContactCarrier &&
                    'Clutter will reach out to carrier to arrange a date'}
                  {load.schedulingOption === Logistics__SchedulingOptionEnum.SoonestAvailableNoRushFee &&
                    'Soonest available date that will not incur a rush fee'}
                  {load.schedulingOption === Logistics__SchedulingOptionEnum.SoonestAvailableRushFeeOk &&
                    'Soonest available date, even if that means paying a rush fee'}
                </div>
              )}
              <div className="col-md-6">
                <h3>Reference Info</h3>
                <ul className="list-unstyled">
                  <li>
                    <strong>Release Number:</strong>
                    {` ${load.releaseNumber || ''}`}
                  </li>
                  {load.amazonReferenceNumber && (
                    <li>
                      <strong>Amazon Reference Number:</strong>
                      {` ${load.amazonReferenceNumber}`}
                    </li>
                  )}
                  {load.reference1 && (
                    <li>
                      <strong>Reference 1:</strong>
                      {` ${load.reference1}`}
                    </li>
                  )}
                  {load.reference2 && (
                    <li>
                      <strong>Reference 2:</strong>
                      {` ${load.reference2}`}
                    </li>
                  )}
                  {load.reference3 && (
                    <li>
                      <strong>Reference 3:</strong>
                      {` ${load.reference3}`}
                    </li>
                  )}
                  {load.reference4 && (
                    <li>
                      <strong>Reference 4:</strong>
                      {` ${load.reference4}`}
                    </li>
                  )}
                  {load.reference5 && (
                    <li>
                      <strong>Reference 5:</strong>
                      {` ${load.reference5}`}
                    </li>
                  )}
                </ul>
              </div>
            </div>
            {load.type === LoadTypeEnum.Outbound && (
              <div className="row">
                {load.shipTo && (
                  <div className="col-md-6">
                    <AddressInfo kind="Ship To" address={load.shipTo} />
                  </div>
                )}
                {load.billTo && (
                  <div className="col-md-6">
                    <AddressInfo kind="Bill To" address={load.billTo} />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Panel.Body>
      <Panel.Footer align="right">
        <Permission resource={LOGISTICS.resource} action={LOGISTICS.actions.update} denied={null}>
          {load.state === 'completed' && load.type === LoadTypeEnum.Inbound && (
            <Button kind="primary" type="submit" onClick={reOpenLoad} loading={loadingReopen}>
              Reopen
            </Button>
          )}
          <Spacer width="8px" />
          <Button
            kind="primary"
            type="submit"
            onClick={() => setCancelModalVisible(true)}
            loading={loadingCancel}
            disabled={load.state === 'canceled'}
          >
            Cancel
          </Button>
          <Spacer width="8px" />
        </Permission>
        <Permission resource={LOGISTICS.resource} action={LOGISTICS.actions.update} denied={null}>
          <Button
            kind="primary"
            type="submit"
            onClick={() => (window.location.href = logisticsEditLoadURL({ id: load.id }))}
          >
            Edit
          </Button>

          {load.requiresAgentReview && (
            <Button kind="primary" onClick={() => setRequestReviewedModalVisible(true)}>
              Mark as Reviewed
            </Button>
          )}
        </Permission>
      </Panel.Footer>
      {cancelModalVisible && (
        <ConfirmationModal
          title="Are you sure you want to cancel the load?"
          acceptButton="Yes, cancel this load"
          declineButton="No, do not cancel this load"
          onConfirm={() => {
            cancelLoad();
            setCancelModalVisible(false);
          }}
          onClose={() => setCancelModalVisible(false)}
        />
      )}
      {requestReviewedModalVisible && (
        <LoadRequestReviewedModal
          load={{ id: load.id, number: load.number, customerName: load.account.customer.name }}
          onClose={() => setRequestReviewedModalVisible(false)}
        />
      )}
    </Panel>
  );
};
